import React, {useContext, useEffect, useState} from "react"
import {useRouter} from "next/router"
import DataContext from "../../context/DataContext"
import {formatImageSrc, getContentFields, strapiFieldKeys} from "../../strapi/strapiHelpers"
import Link from "next/link"
import {useTranslation} from "react-i18next"
import Typography from "../reusable/Typography/Typography"
import {X} from "react-bootstrap-icons"
import Modal from "../reusable/components/Modal/Modal"
import AnimationComponent from "../reusable/components/AnimationComponent/AnimationComponent"
import {premiumBrands} from "../../constants"

type Props = {
    selectedBrand: string
    selectedCategory: string
}

const BrandInfoPLP: React.FC<Props> = ({selectedBrand, selectedCategory}) => {
	const {t} = useTranslation(["common", "filters"])

	const {brands, pageProps: {strapiContent: {data: {collection_pages: collectionPages}}}} = useContext(DataContext)
	const router = useRouter()

	const [strapiData, setStrapiData] = useState<any>(null)
	const [showFullText, setShowFullText] = useState(false)

	const currentBrand = brands?.find(brand => brand?.fields?.slug === selectedBrand)

	useEffect(() => {
		if (selectedBrand) {
			const data = collectionPages?.find((page: any) => page.attributes.slug === selectedBrand)
			const brandData = getContentFields(data, [strapiFieldKeys.text])
			setStrapiData(brandData)
		}
	}, [selectedBrand, router.locale])

	const getWordCount = (text: string) => {
		return text ? text.trim().split(/\s+/).length : 0
	}

	if (!currentBrand) {
		return null
	}

	const {slug, icon, menuLabel} = currentBrand.fields
	const logoImageUrl = icon?.image?.data ? formatImageSrc(icon?.image?.data?.attributes.url) : ""
	const wordCount = getWordCount(strapiData?.text?.content)
	const isPremiumBrand = premiumBrands.includes(slug)
	return (
		<section className="d-flex flex-column flex-md-row col-12"
			style={{minHeight: 92, columnGap: 40}}>
			<div style={{height: 52}}>
				{isPremiumBrand ? <Link href={router.locale === "de" ? ` /brand-world/${slug}` : ` /fr/brand-world/${slug}` }>
					<div
						className="logo-space col-12 position-relative"
						style={
							logoImageUrl ?
								{
									backgroundImage: `url(${logoImageUrl})`,
									backgroundPosition: "center center",
									backgroundSize: "contain",
									backgroundRepeat: "no-repeat",
									cursor: isPremiumBrand ? "pointer" : "normal",
									height: "100%",
									width: 141
								} :
								null
						}
					></div>
				</Link> : <div
					className="logo-space col-12 position-relative"
					style={
						logoImageUrl ?
							{
								backgroundImage: `url(${logoImageUrl})`,
								backgroundPosition: "center center",
								backgroundSize: "contain",
								backgroundRepeat: "no-repeat",
								cursor: "pointer",
								height: "100%",
								width: 141
							} :
							null
					}
				></div>}
			</div>
			<div className="info"
				style={{width: "100%"}}>
				<div className="d-none d-md-block">
					<Typography variant="heading1Black"
						semanticTag="h1" >
						{menuLabel}
					</Typography>
					<div className="d-flex position-relative pe-3">
						<Typography variant="bodySm"
							semanticTag="p"
							className="text-section brand-description-truncate multiline-truncate indent-p">
							{strapiData?.text?.content}
						</Typography>
						{wordCount > 45 &&
						<div className="read-more-link"
							onClick={() => setShowFullText(true)}
							style={{right: "50px"}}>
							<div className={"d-flex pointer"}>
								<Typography variant="bodySmBold"
									semanticTag="span">
									{t("read-more")}
								</Typography>
							</div>
						</div>}
					</div>
				</div>
				<div className="d-md-none">
					<Typography variant="bodyLg"
						semanticTag="h3"
						style={{fontWeight: 900}}>
						{menuLabel}
					</Typography>
					<div className="d-flex position-relative pe-3">
						<Typography variant="bodyXSm"
							semanticTag="p"
							className="text-section brand-description-truncate multiline-truncate indent-p">
							{strapiData?.text?.content}
						</Typography>
						<div className="read-more-link"
							onClick={() => setShowFullText(true)}
							style={{right: "20px"}}>
							<div className={"d-flex pointer"}>
								<Typography variant="bodyXSmBold"
									semanticTag="span">
									{t("read-more")}
								</Typography>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Desktop Modal for full text */}
			<div className="d-none d-md-block"><Modal isOpen={showFullText}
				closeModal={() => setShowFullText(false)}
				closeOnTouch>
				<div className={"position-relative bg-white text-dark p-4 pe-5"}
					style={{borderRadius: 16, width: 500, minHeight: 200}}>
					<button onClick={() => setShowFullText(false)}
						className={"d-none d-md-block position-absolute btn btn-light btn-outline-light rounded-circle p-0 top-0 end-0"}
						style={{
							marginTop: "2%",
							marginRight: "3%",
							zIndex: 2
						}}>
						<X size={30}
							color={"black"} />
					</button>

					<Typography variant="bodyLg"
						semanticTag="p">
						{strapiData?.text?.content}
					</Typography>
				</div>
			</Modal></div>
			{/* Mobile Popup for full text */}
			<div className="d-block d-md-none"
				style={{zIndex: 20010}}>
				<div className={`position-fixed top-0 start-0 bottom-0 end-0 bg-light-dark-transparent ${!showFullText && "d-none"}`}
					style={{
						zIndex: 20002
					}}
					onClick={() => setShowFullText(showFullText => !showFullText)}/>
				<AnimationComponent type="slideUp"
					inProp={showFullText}>
					<div className={"position-fixed px-3 py-4 bg-white start-0 bottom-0 end-0"}
						style={{
							zIndex: 999999,
							borderRadius: "16px 16px 0px 0px",
							border: "1px solid #f4f4f4"
						}}>
						<div>
							<Typography variant="bodyLg"
								semanticTag="p"
								className="pe-4 me-2">
								{strapiData?.text?.content}
							</Typography>
							<button onClick={() => setShowFullText(false)}
								className={"position-absolute btn btn-light btn-outline-light rounded-circle p-0 top-0 end-0"}
								style={{
									marginTop: "3%",
									marginRight: "3%",
									zIndex: 2
								}}>
								<X size={30}
									color={"black"}/>
							</button>
						</div>

					</div>
				</AnimationComponent>
			</div>
		</section>
	)
}

export default BrandInfoPLP
